import type { EntityType } from '@erp-attach/shared';

export interface RecentEntry {
  kind: EntityType;
  number: string;
  label: string;
  href: string;
  visitedAt: string;
}

export interface RecentDocument {
  id: string;
  title: string;
  filename: string | null;
  /** Record the document belongs to, for the subtitle. */
  record: string | null;
  href: string;
  visitedAt: string;
}

export const RECENT_STORAGE_KEY = 'erp-attach.recent';
export const RECENT_DOCS_STORAGE_KEY = 'erp-attach.recent-documents';
const MAX_RECENT = 10;
const MAX_RECENT_DOCS = 12;

function readList<T extends { href: string }>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]).filter((e) => e && typeof e.href === 'string') : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: unknown[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
}

export function getRecent(): RecentEntry[] {
  return readList<RecentEntry>(RECENT_STORAGE_KEY);
}

export function addRecent(entry: Omit<RecentEntry, 'visitedAt'>): void {
  const next = [{ ...entry, visitedAt: new Date().toISOString() }, ...getRecent().filter((e) => e.href !== entry.href)].slice(
    0,
    MAX_RECENT,
  );
  writeList(RECENT_STORAGE_KEY, next);
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(RECENT_STORAGE_KEY);
    localStorage.removeItem(RECENT_DOCS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getRecentDocuments(): RecentDocument[] {
  return readList<RecentDocument>(RECENT_DOCS_STORAGE_KEY);
}

export function addRecentDocument(entry: Omit<RecentDocument, 'visitedAt'>): void {
  const next = [
    { ...entry, visitedAt: new Date().toISOString() },
    ...getRecentDocuments().filter((e) => e.href !== entry.href),
  ].slice(0, MAX_RECENT_DOCS);
  writeList(RECENT_DOCS_STORAGE_KEY, next);
}

/** Subscribe to changes made through this module (same tab) or another tab. */
export function onRecentChange(listener: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === null || e.key === RECENT_STORAGE_KEY || e.key === RECENT_DOCS_STORAGE_KEY) listener();
  };
  window.addEventListener('storage', handler);
  window.addEventListener('erp-attach:recent', listener);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('erp-attach:recent', listener);
  };
}

export function notifyRecentChange(): void {
  try {
    window.dispatchEvent(new Event('erp-attach:recent'));
  } catch {
    /* ignore */
  }
}
